import { useMemo, type CSSProperties } from 'react';
import { Tooltip } from 'antd';
import type { ColumnsType } from 'antd/es/table';

import { resolveFieldLabel, type FieldValueLabelMaps } from '../../../utils/resolveFieldLabel';
import type { PromoLibraryColumnConfig } from '../../../config/promoLibraryConfig';
import { AttributeCell, KpiCell, formatCellTooltip } from '../../cellRenderers';
import type { EditKpiTarget, PromoLibraryCellValue, PromoLibraryRow, SortOrder } from '../../types';

const BASE_CELL_STYLE: CSSProperties = { padding: '8px 16px', borderColor: '#E4E4E7' };
const BODY_CELL_STYLE: CSSProperties = { ...BASE_CELL_STYLE, color: '#52525C' };
const MUTED_BODY_CELL_STYLE: CSSProperties = { ...BODY_CELL_STYLE, backgroundColor: '#FAFAFA' };
const HEADER_CELL_STYLE: CSSProperties = { ...BASE_CELL_STYLE, backgroundColor: '#FAFAFA' };

const LINE_CLAMP_STYLE: CSSProperties = {
    display: '-webkit-box',
    WebkitLineClamp: 3,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
    wordBreak: 'break-word',
};

const SORT_DIRECTIONS = ['ascend', 'descend'] as const;

interface UsePromoLibraryColumnsParams {
    /** Config columns already narrowed to the ones currently visible. */
    columns: PromoLibraryColumnConfig[];
    fieldValueLabelMaps: FieldValueLabelMaps;
    showChange: boolean;
    sortBy: string;
    sortOrder: SortOrder;
    /** Called when the user clicks an editable KPI cell. */
    onEdit?: (target: EditKpiTarget) => void;
}

/**
 * Extracts the current cell value(s) from a row so the edit modal can display
 * them.  Range KPIs store `[min, max]`; scalar KPIs store `[current]`.
 */
const extractCurrentValue = (raw: unknown): (number | string | boolean | null)[] => {
    if (raw === null || raw === undefined) return [];
    if (typeof raw === 'object' && raw !== null) {
        const cell = raw as PromoLibraryCellValue;
        return cell.current !== null && cell.current !== undefined ? [cell.current] : [];
    }
    return [raw as number | string];
};

/**
 * Translates the config column list into antd columns. Sorting is server-side:
 * every column reports `sorter: true` and the active column's arrow is driven by
 * the page's sort state rather than antd's internal one.
 */
function usePromoLibraryColumns({
    columns,
    fieldValueLabelMaps,
    showChange,
    sortBy,
    sortOrder,
    onEdit,
}: UsePromoLibraryColumnsParams): ColumnsType<PromoLibraryRow> {
    return useMemo(
        () =>
            columns.map((column) => {
                const isMuted = !!column.muted;
                const isEditable = column.kind === 'kpi' && !!column.editConfig && !!onEdit;

                return {
                    key: column.key,
                    dataIndex: column.key,
                    title: (
                        <Tooltip title={column.label} placement="topLeft">
                            <span
                                className={`text-sm font-medium ${isMuted ? 'text-[#71717B]' : 'text-[#18181B]'}`}
                                style={LINE_CLAMP_STYLE}
                            >
                                {column.label}
                            </span>
                        </Tooltip>
                    ),
                    width: column.width,
                    fixed: column.fixed,
                    align: column.align ?? (column.kind === 'kpi' ? 'right' : 'left'),
                    sorter: true,
                    sortOrder: sortBy === column.key ? (sortOrder === 'asc' ? 'ascend' : 'descend') : null,
                    sortDirections: [...SORT_DIRECTIONS],
                    onCell: () => ({ style: isMuted ? MUTED_BODY_CELL_STYLE : BODY_CELL_STYLE }),
                    onHeaderCell: () => ({ style: HEADER_CELL_STYLE }),
                    render: (value: unknown, record: PromoLibraryRow) => {
                        if (column.kind === 'kpi') {
                            const tooltip = formatCellTooltip(
                                value as PromoLibraryCellValue,
                                column.format,
                                column.valueFormat,
                            );
                            const handleEdit =
                                isEditable && record.row_id
                                    ? () =>
                                          onEdit({
                                              kpiKey: column.key,
                                              kpiLabel: column.label,
                                              currentValue: extractCurrentValue(value),
                                              rowId: record.row_id!,
                                              rowLabel: record.row_id!,
                                          })
                                    : undefined;
                            return (
                                <Tooltip title={tooltip} placement="topLeft">
                                    <div
                                        style={isEditable ? { ...LINE_CLAMP_STYLE, cursor: 'pointer' } : LINE_CLAMP_STYLE}
                                        onClick={handleEdit}
                                        role={handleEdit ? 'button' : undefined}
                                        tabIndex={handleEdit ? 0 : undefined}
                                    >
                                        <KpiCell
                                            value={value as PromoLibraryCellValue}
                                            format={column.format}
                                            valueFormat={column.valueFormat}
                                            showChange={showChange}
                                            muted={isMuted}
                                        />
                                    </div>
                                </Tooltip>
                            );
                        }

                        const displayValue = column.resolveLabelFrom
                            ? resolveFieldLabel(value as string, fieldValueLabelMaps, column.resolveLabelFrom)
                            : ((value as string) ?? '');

                        return (
                            <Tooltip title={displayValue} placement="topLeft">
                                <div style={LINE_CLAMP_STYLE}>
                                    <AttributeCell value={displayValue} muted={isMuted} />
                                </div>
                            </Tooltip>
                        );
                    },
                } as ColumnsType<PromoLibraryRow>[number];
            }),
        [columns, fieldValueLabelMaps, showChange, sortBy, sortOrder, onEdit],
    );
}

export default usePromoLibraryColumns;
